import cx from "classnames";

import ActionListItem from "@/appComponents/Blocks/Composites/ActionListItem/ActionListItem";
import RecordFieldsTable from "@/appComponents/Blocks/Composites/RecordFieldsTable/RecordFieldsTable";
import { Accordion, Flex, Text } from "@theorgchartdev/storybook";
import { addTalentToPlan, removeTalentFromPlan } from "./TalentPool.utils";
import { getHoverTipContent } from "@/Utils/Misc";
import { GetGroupIconByType } from "../Activity/Activity.utils";
import { getFieldValuePair } from "@/Utils/DataLib";
import { QObjDBObject } from "@coreapp/CoreApp/QObjDBObject";
import { enumOverrideType } from "@coreapp/constants";
import { appstr } from "@coreapp/utils/appstr";
import { sanitizeBlockedValue } from "@coreapp/utils/FormatFieldValue";

const BuildTalentActions = ({ dataTestIdPrefix, state, handlers }) => {
  if (!state.canEdit) {
    return [];
  }

  const actions = [];
  if (state.canAddBranch) {
    actions.push({
      iconName: "user-plus",
      dataTestId: `${dataTestIdPrefix}add-branch`,
      tooltipProps: getHoverTipContent("Add Branch"),
      onClick: handlers.handleAdd(enumOverrideType.add, true)
    });
  }

  if (state.isInPlan) {
    actions.push({
      iconName: "arrow-right-solid",
      dataTestId: `${dataTestIdPrefix}move`,
      tooltipProps: getHoverTipContent("Move within plan"),
      onClick: handlers.handleAdd(enumOverrideType.move)
    });
  } else {
    actions.push({
      iconName: "plus",
      dataTestId: `${dataTestIdPrefix}add`,
      tooltipProps: getHoverTipContent("Add to plan"),
      onClick: handlers.handleAdd(enumOverrideType.add)
    });
  }

  return [
    ...actions,
    {
      iconName: "trash-can",
      dataTestId: `${dataTestIdPrefix}remove`,
      iconProps: { color: "error" },
      tooltipProps: getHoverTipContent("Remove Talent"),
      onClick: handlers.handleRemove
    }
  ];
};

// From QTalentPoolPanel
const fDefaultFields = {
  SupervisorID: "SupervisorID",
  ReportsToName: "Reports To Name",
  Title: "Title",
  Division: "Division",
  Location: "Location"
};

export const TalentDetail = props => {
  const doc = props.planMgr.GetDoc();
  const planInfo = props.planMgr.GetPlanInfo();

  const myRole = props.planMgr.GetPlanRole();
  const canEdit = props.planMgr.CanUpdatePlan();

  const objDB = doc.GetObjDB();
  const boxClass = objDB.GetClassByName("Box");
  const nameDef = boxClass.GetFieldDefByID("Name");
  const nameField = nameDef.GetFieldName();
  const IDFieldDef = boxClass.GetRecordIDFieldDef(false);
  const IDField = IDFieldDef.GetFieldName();

  const talentName = sanitizeBlockedValue(props.talent[nameField]);
  const talentID = props.talent[IDField];
  const headerStr = talentName ? `${talentName} (${talentID})` : appstr("Blank");
  const isInPlan = props.talent["__inplan__"] === true;
  const hasSubs = props.talent["Span Of Control"] > 0;
  const canAddBranch = !isInPlan && hasSubs;

  const handleRemove = () => {
    removeTalentFromPlan(props.planMgr, talentID);
  };

  const handleAdd = (type, branchAdd) => () => {
    addTalentToPlan(props.planMgr, talentID, type, branchAdd);
  };

  const state = { canAddBranch, canEdit, isInPlan };

  const handlers = {
    handleAdd,
    handleRemove
  };

  const BuildData = () => {
    const data = [];

    const personIDFieldDef = boxClass.GetRecordIDFieldDef();
    const personIDFieldName = personIDFieldDef.GetFieldName();
    const personID = props.talent[personIDFieldName];
    let dbObj = props.planMgr.fCloneBaseCache[personID];

    if (!dbObj) {
      // Not all records in the talent pool exist in the plan
      dbObj = new QObjDBObject();
      dbObj.InitObject(boxClass);
      boxClass.ProcessObject(dbObj, props.talent);
    }

    Object.values(fDefaultFields).forEach(fieldID => {
      let fieldVal;
      let fieldDef = boxClass.GetFieldDefByID(fieldID);
      let fieldName = fieldDef?.GetFieldName();

      if (fieldID === fDefaultFields.ReportsToName) {
        fieldDef = boxClass.GetFieldDefByID(fDefaultFields.SupervisorID);
        const superIDFieldName = fieldDef.GetFieldName();
        const superID = props.talent[superIDFieldName];
        const superDBObj = props.planMgr.fCloneBaseCache[superID];
        if (!superDBObj) {
          return;
        }

        fieldVal = sanitizeBlockedValue(superDBObj.SmartGetFieldValue("Name"));
      } else {
        fieldVal = sanitizeBlockedValue(dbObj.SmartGetFieldValue(fieldID));
      }

      data.push(getFieldValuePair(fieldName, fieldVal, false));
    });

    // We only want holder data so we pass specific field type
    const additionalFields = planInfo?.fields?.holder ?? [];
    (additionalFields ?? []).forEach(fieldID => {
      let fieldVal = sanitizeBlockedValue(dbObj.SmartGetFieldValue(fieldID));
      const fieldDef = dbObj.GetFieldDefByName(fieldID);
      if (!fieldDef) {
        return;
      }
      const fieldName = fieldDef.GetFieldName();
      data.push(getFieldValuePair(fieldName, fieldVal, false));
    });

    return data;
  };

  let type = enumOverrideType.add;
  if (isInPlan) {
    type = enumOverrideType.move;
  }

  const dataTestId = `talent-pool-entry-${talentName}`;

  return (
    <div
      className={cx(
        "sidebar-content-item",
        props.includeDivider ? "sidebar-content-item--divider" : null
      )}
    >
      <Accordion
        name={
          <ActionListItem
            dataTestId={dataTestId + "-header"}
            label={
              <Flex gap={"4px"}>
                {GetGroupIconByType(type)}
                <Text modifier={"bold"} size={"small"}>
                  {headerStr}
                </Text>
              </Flex>
            }
            labelProps={{ size: "small", modifier: "bold" }}
            actions={BuildTalentActions({ dataTestIdPrefix: dataTestId + "-", state, handlers })}
          />
        }
        beforeContent={<RecordFieldsTable data={BuildData()} />}
      />
    </div>
  );
};
